import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  TextField,
  CircularProgress,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { debounce, isEmpty } from "lodash";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { ResponseTypeList } from "src/Pages/Company/Absences/api/api";
import {
  IEmployeeDetails,
  IEmployeesAutocomplete,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import __ from "src/utils/Translations";
import EmployeesAutocompleteOption from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompleteOption";
import { getInputStyles } from "./EmployeesAutocomplete.styles";
import EmployeesAutocompleteAdornment from "./EmployeesAutocompleteAdornment";

interface IEmployeesAutocompleteProps extends IEmployeesAutocomplete {
  value: IEmployeeDetails | null;
  onChange: (arg: IEmployeeDetails | null) => void;
}

const EmployeesAutocomplete: FC<IEmployeesAutocompleteProps> = ({
  name,
  label,
  enabled,
  value,
  queryKey,
  queryFn,
  onChange,
  additionalQueryParams = {},
  required = false,
  hasDefaultValue = false,
}) => {
  const companyId = useCompanyId();

  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRemovedAdornment, setIsRemovedAdornment] = useState(
    !hasDefaultValue
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<ResponseTypeList<IEmployeeDetails>>({
      queryKey: [queryKey, search, additionalQueryParams],
      queryFn: ({ pageParam }) =>
        queryFn({
          companyId,
          pageParam,
          name: search || "",
          ...additionalQueryParams,
        }),
      getNextPageParam: (lastPage: ResponseTypeList<IEmployeeDetails>) => {
        if (lastPage.page < lastPage.totalPages) {
          return lastPage.page + 1;
        }

        // Should return undefined in case there is no next page available (based on tanstack docs)
        // eslint-disable-next-line no-undefined
        return undefined;
      },
      enabled,
      initialPageParam: null,
    });

  const options = useMemo(
    () => data?.pages.flatMap((page) => page.content) || [],
    [data?.pages]
  );

  const debouncedSearch = useRef(
    debounce((__: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(debouncedSearch, [debouncedSearch]);

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    const scrollPercentage =
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight;

    // Load more when user has scrolled 80% of the way down
    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      <Autocomplete
        options={options}
        getOptionLabel={(option) =>
          !isEmpty(option) ? `${option?.firstName} ${option?.lastName}` : ""
        }
        getOptionKey={(option) => option.employeeId}
        isOptionEqualToValue={(option, selectedValue) =>
          option.employeeId === selectedValue?.employeeId
        }
        loading={isLoading || isFetchingNextPage}
        value={value || null}
        disableListWrap
        disablePortal
        onOpen={() => setSearch("")}
        onChange={(__, newValue) => {
          setIsTyping(false);

          setIsRemovedAdornment(!newValue);

          onChange(newValue);
        }}
        onBlur={() => setIsTyping(false)}
        onInputChange={(event, newInputValue, reason) => {
          if (reason !== "reset") {
            setIsTyping(true);
            handleSearchChange(event, newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            id={name}
            label={label}
            required={required}
            sx={getInputStyles(isTyping, false)}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps?.input,
                startAdornment: (
                  <InputAdornment position="start">
                    {!isTyping && !isRemovedAdornment && (
                      <EmployeesAutocompleteAdornment data={value} />
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {isLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {(params.slotProps?.input as any)?.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <EmployeesAutocompleteOption
            liProps={props}
            option={option}
            showInactiveLabel
          />
        )}
        slotProps={{
          listbox: {
            onScroll: handleScroll,
            style: {
              maxHeight: "300px",
              overflowY: "auto",
            },
          },
        }}
        data-testid={`absence-request-form-field__${name}`}
      />
    </>
  );
};

export default EmployeesAutocomplete;
